/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
*/
import Cookies from 'js-cookie';
import { TranslocoService } from '@jsverse/transloco';
import { BulbFetchUserService } from '../common/fetch-user.service';
import { ErrorService } from '../common/error.service';

/**
 * This file contains exported methods related to the app module.
 * This is the standard location for stand along factory, helper, utility functions that are not appropriate to
 * put into other components, services, etc.
 */

// This is a minimal type implementation of the TranslocoService.
// Allowing this type simplifies mocking this service in tests.
type MockTranslocoService = { setActiveLang: (arg0: any) => void; load: (arg0: any) => void; };

export const availableLangs = ['de', 'en', 'es', 'fi', 'fr', 'nl', 'no', 'pt', 'sv'];

/**
 * Fetch the current user, used during bootstrapping of the app.
 * @param fetchCurrentUser
 */
export function fetchCurrentUserServiceFactory(
  fetchCurrentUser: BulbFetchUserService,
  transloco: TranslocoService | MockTranslocoService,
): Function {
  return async () => {
    const user = await fetchCurrentUser.fetchUser();
    let lang = user.language.toLowerCase();
    // If user is not logged in try to find locale from cookie/browser.
    if (user.id === null) {
      lang = Cookies.get('language') || navigator.language;
      // If locale is not found in the cookies, we take the browser locale and it is in the en-US format.
      // So we need to split it to get the lang code.
      if (lang.indexOf('-') > 0) {
        lang = lang.split('-')[0];
      }

      // Fallback language for anonymous users is English.
      // Logged-in users have their language set in the database.
      // Transloco inline-loaders used in downgraded components in the AJS context are initialized
      // before the initial language is set, and do not use Transloco's fallback stragegy.
      // This ensures that the language is set to one of our supported languages.
      if (!availableLangs.includes(lang)) {
        lang = 'en';
      }
    }

    transloco.setActiveLang(lang);
    transloco.load(lang);
    return user;
  };
}

/**
 * Function to generate an ObjectId in the same way that mongodb would
 * WARNING: These generated objectIds should not be used for domain object ids as they are not guaranteed
 * to be globally unique. They should only be used within a page or someplace whre isolated uniqueness is
 * sufficient.
 */
export function generateObjectId() {
  const timestamp = (new Date().getTime() / 1000 | 0).toString(16);
  return timestamp + 'xxxxxxxxxxxxxxxx'.replace(/[x]/g, () => (Math.random() * 16 | 0).toString(16)).toLowerCase();
}
